import React from 'react';
import {
  Activity,
  ArrowRight,
  ArrowUpRight,
  BookOpen,
  Bot,
  Box,
  CheckCircle2,
  Cloud,
  Code2,
  Copy,
  FlaskConical,
  GitBranch,
  Globe,
  Hammer,
  HardDrive,
  Image as ImageIcon,
  Inbox,
  KeyRound,
  Layers,
  MessageSquare,
  MonitorCog,
  Palette,
  Play,
  Search,
  Server,
  ShieldCheck,
  Sparkles,
  Terminal,
  Video,
  Wrench,
  Zap,
  Library,
} from 'lucide-react';

import TerminalCast from './TerminalCast';
type IconType = React.ComponentType<{ size?: number; className?: string }>;

interface ProductCard {
  title: string;
  eyebrow: string;
  description: string;
  href: string;
  icon: IconType;
}

interface ModelLane {
  model: string;
  route: string;
  use: string;
  price: string;
  href: string;
  tools?: { label: string; href: string }[];
}

interface ArtShowcaseItem {
  title: string;
  provider: string;
  model: string;
  prompt: string;
  imageUrl: string;
  href: string;
}

interface MediaCapability {
  title: string;
  label: string;
  body: string;
  href: string;
  icon: IconType;
}

const productCards: ProductCard[] = [
  {
    title: 'Create Apps',
    eyebrow: 'start fast',
    description: 'New project with repos, models, and storage.',
    href: '/projects',
    icon: Box,
  },
  {
    title: 'Coding Agents',
    eyebrow: 'autonomous',
    description: 'Branches, codes, runs tests, opens a PR.',
    href: '/agent',
    icon: Bot,
  },
  {
    title: 'Skills',
    eyebrow: 'searchable library',
    description: 'Search and author agent skills, add to any agent.',
    href: '/skills',
    icon: Library,
  },
  {
    title: 'AI Use Cases',
    eyebrow: '50+ tools',
    description: 'Staging, product shots, voice, music, video on credits.',
    href: '/ai',
    icon: Sparkles,
  },
  {
    title: 'Repos & PRs',
    eyebrow: 'git hosting',
    description: 'Hosted git, branch previews, agents that fix CI.',
    href: '/repos',
    icon: GitBranch,
  },
  {
    title: 'Chat & Research',
    eyebrow: 'chat & search',
    description: 'Chat, files, cited deep research.',
    href: '/chat',
    icon: MessageSquare,
  },
  {
    title: 'Papers',
    eyebrow: 'free research search',
    description: 'Free search: papers, methods, datasets, code.',
    href: '/papers',
    icon: BookOpen,
  },
  {
    title: 'Search the Web',
    eyebrow: 'agent tools',
    description: 'Live web and index search, with sources.',
    href: '/search',
    icon: Search,
  },
  {
    title: 'Live Art Playground',
    eyebrow: 'media lab',
    description: 'Images, video, music, speech, 3D in the browser.',
    href: '/gateway#playground',
    icon: Palette,
  },
  {
    title: 'Browse Art',
    eyebrow: 'prompt search',
    description: 'Search prompts, models, and styles.',
    href: '/art',
    icon: ImageIcon,
  },
  {
    title: 'Fusion',
    eyebrow: 'multi-model',
    description: 'One prompt, several models, one fused answer, an API per panel.',
    href: '/fusion',
    icon: Sparkles,
  },
  {
    title: 'Models',
    eyebrow: 'model routing',
    description: 'One API for chat, image, video, code; auto-route or pin a provider.',
    href: '/gateway',
    icon: Layers,
  },
  {
    title: 'Hosting',
    eyebrow: 'deploy',
    description: 'Static sites, containers, APIs, GPU endpoints.',
    href: '/deploys',
    icon: Cloud,
  },
  {
    title: 'Cog Studio',
    eyebrow: 'GPU inference',
    description: 'Deploy Cog containers as scale-to-zero GPU endpoints.',
    href: '/cogs',
    icon: MonitorCog,
  },
  {
    title: 'Training',
    eyebrow: 'fine-tuning',
    description: 'Fine-tune open models, then deploy the weights.',
    href: '/training',
    icon: FlaskConical,
  },
  {
    title: 'Build Studio',
    eyebrow: 'container CI',
    description: 'Build Docker images, push to your registry, run them.',
    href: '/builds',
    icon: Hammer,
  },
  {
    title: 'Queues',
    eyebrow: 'task queues',
    description: 'JSON jobs with retries, dead-letter, and spend caps.',
    href: '/queues',
    icon: Inbox,
  },
  {
    title: 'Tools',
    eyebrow: 'free + AI',
    description: 'Prompt evals, compare, costs, one credit balance.',
    href: '/tools',
    icon: Wrench,
  },
];

const heroBadgeLinks: { label: string; href: string }[] = [
  { label: 'images', href: '/text-to-image' },
  { label: 'video', href: '/video' },
  { label: 'sites', href: '/sites' },
  { label: 'agents', href: '/agent' },
  { label: 'models', href: '/models' },
  { label: 'hosting', href: '/deploys' },
];

const heroPillars: { icon: IconType; label: string; href: string }[] = [
  { icon: ImageIcon, label: 'Images', href: '/text-to-image' },
  { icon: Video, label: 'Video', href: '/video' },
  { icon: Globe, label: 'Live sites', href: '/sites' },
  { icon: Bot, label: 'Coding agent', href: '/agent' },
  { icon: Palette, label: 'Art playground', href: '#art-playground' },
  { icon: Cloud, label: 'Deploys', href: '/deploys' },
];

const modelLanes: ModelLane[] = [
  { model: 'app/auto', route: 'frontier', use: 'general chat, planning, mixed workloads', price: 'auto-routed', href: '/chat' },
  { model: 'app/auto-code', route: 'agentic', use: 'repo edits, PR repair, migrations', price: 'best-fit', href: '/agent' },
  { model: 'app/auto-fast', route: 'latency', use: 'realtime UX, chat, tools', price: 'low-latency', href: '/chat' },
  { model: 'app/search-deep', route: 'research', use: 'web search, papers, citations, extraction', price: '$0.001+ per search', href: '/gateway#playground' },
  {
    model: 'app/auto-image',
    route: 'media',
    use: 'images, edits, art workflows',
    price: 'per output',
    href: '/text-to-image',
    tools: [
      { label: 'text-to-image', href: '/text-to-image' },
      { label: 'art search', href: '/art' },
    ],
  },
  { model: 'app/auto-video', route: 'media', use: 'video clips, image-to-video, motion tests', price: 'per job', href: '/video' },
  {
    model: 'app/music',
    route: 'media',
    use: 'music, loops, ambience, foley, sound effects',
    price: 'per job',
    href: '/music',
    tools: [
      { label: 'music space', href: '/music' },
      { label: 'audio editor', href: '/audio' },
      { label: 'gateway playground', href: '/gateway#playground-music' },
    ],
  },
  {
    model: 'app/3d',
    route: 'media',
    use: 'image-to-3D, text-to-3D, rigging, retexture',
    price: 'per asset',
    href: '/3d-editor',
    tools: [
      { label: 'image-to-3D', href: '/image-to-3d' },
      { label: 'text-to-3D', href: '/text-to-3d' },
      { label: 'rigging', href: '/3d-editor' },
      { label: 'retexture', href: '/3d-editor' },
    ],
  },
  { model: 'app/model-hosted', route: 'hosted', use: 'custom hosted model endpoints', price: 'from $0.228/hr', href: '/cogs' },
];

const artShowcase: ArtShowcaseItem[] = [
  {
    title: 'Archive Chapel',
    provider: 'Fal',
    model: 'FLUX Klein 4B',
    prompt: 'A brutalist chapel grown from translucent salt crystals on the edge of a frozen black lake.',
    imageUrl: 'https://openpathsstatic.openpaths.io/static/uploads/landing/art-playground/fal/fal-klein-archive-chapel.webp',
    href: '/models?q=flux%20klein',
  },
  {
    title: 'Gravity Orchard',
    provider: 'Fal',
    model: 'FLUX Schnell',
    prompt: 'An orchard where every fruit is a tiny moon with its own orbit, harvested by lantern light.',
    imageUrl: 'https://openpathsstatic.openpaths.io/static/uploads/landing/art-playground/fal/fal-schnell-gravity-orchard.webp',
    href: '/models?q=flux%20schnell',
  },
  {
    title: 'Ocean Typewriter',
    provider: 'Together AI',
    model: 'Stable Diffusion 3',
    prompt: 'A vintage typewriter on the seafloor releasing schools of silver punctuation fish.',
    imageUrl: 'https://openpathsstatic.openpaths.io/static/uploads/landing/art-playground/together-ai/together-sd3-ocean-typewriter.webp',
    href: '/models?q=stable%20diffusion',
  },
  {
    title: 'Lantern Koi Station',
    provider: 'Netwrck',
    model: 'ZImage',
    prompt: 'Anime train platform floating above a koi pond at blue hour with paper lantern reflections.',
    imageUrl: 'https://openpathsstatic.openpaths.io/static/uploads/landing/art-playground/netwrck/zimage-lantern-koi-station.webp',
    href: '/models?q=zimage',
  },
];

const mediaCapabilities: MediaCapability[] = [
  {
    title: 'Image generation',
    label: 'GPT Image · FLUX · RA1 · ZImage · SD3',
    body: 'Prompt, edit, and route across OpenAI, Fal, Netwrck, and Together.',
    href: '/gateway#playground',
    icon: ImageIcon,
  },
  {
    title: 'Video generation',
    label: 'Sora · Hailuo · Seedance · Wan · LTX',
    body: 'Text-to-video and image-to-video on the same key and credits.',
    href: '/gateway#playground',
    icon: Video,
  },
  {
    title: '3D generation',
    label: 'Pixal3D · Meshy · Tripo · Trellis',
    body: 'Text or image to textured GLB; rig and retexture in 3D Studio.',
    href: '/3d-editor',
    icon: Box,
  },
  {
    title: 'Fusion',
    label: 'app.nz Fusion · panel + judge',
    body: 'Send one prompt to several models; fuse the outputs.',
    href: '/fusion',
    icon: Sparkles,
  },
  {
    title: 'Browse art prompts',
    label: 'Art search · tags · prompt reuse',
    body: 'Search by style, scene, or model; reuse the prompt.',
    href: '/art',
    icon: Search,
  },
  {
    title: 'Provider routing',
    label: '20+ providers · one balance',
    body: 'OpenAI, Anthropic, Google, xAI, Fal, Together, and 20+ more.',
    href: '/gateway#providers',
    icon: Layers,
  },
];

const providerBadges = [
  { label: 'OpenAI', provider: 'openai' },
  { label: 'Anthropic', provider: 'anthropic' },
  { label: 'Google', provider: 'google' },
  { label: 'xAI', provider: 'xai' },
  { label: 'DeepSeek', provider: 'deepseek' },
  { label: 'Mistral', provider: 'mistral' },
  { label: 'Groq', provider: 'groq' },
  { label: 'Together AI', provider: 'together' },
  { label: 'OpenRouter', provider: 'openrouter' },
  { label: 'Fal', provider: 'fal' },
  { label: 'Netwrck', provider: 'netwrck' },
  { label: 'eBank.nz', provider: 'ebank' },
  { label: 'MiniMax', provider: 'minimax' },
  { label: 'CuteDSL', provider: 'cutedsl' },
  { label: 'Z.AI', provider: 'zai' },
  { label: 'Fireworks', provider: 'fireworks' },
  { label: 'NVIDIA', provider: 'nvidia' },
  { label: 'Exa', provider: 'exa' },
  { label: 'Papers', provider: 'papers' },
  { label: 'Text-Generator.io', provider: 'textgenerator' },
  { label: 'Nous', provider: 'nous' },
];

const INSTALL_COMMAND = 'curl -fsSL https://app.nz/cli.sh | sh';

function copyInstallCommand(): Promise<boolean> {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    return navigator.clipboard.writeText(INSTALL_COMMAND).then(
      () => true,
      () => false,
    );
  }
  return Promise.resolve(false);
}

const terminalCards = [
  {
    title: 'Create a site',
    command: '$ app sites create ai-dashboard --title "AI Dashboard"',
    lines: ['Creating hosted site', 'Configured title', 'Ready for files', 'https://ai-dashboard.app.nz'],
  },
  {
    title: 'Run an agent',
    command: '$ app agent run --prompt "add usage analytics" --repo acme/ai-dashboard',
    lines: ['Planning task', 'Creating branch feat/usage-analytics', 'Writing code and tests', 'Opening PR #42'],
  },
  {
    title: 'Deploy an app',
    command: '$ app apps deploy ./ai-dashboard',
    lines: ['Packaging source', 'Running build on the server', 'Release running', 'https://ai-dashboard.app.nz'],
  },
  {
    title: 'Ask a model',
    command: '$ app chat "why is this deploy failing?" --model app/auto',
    lines: ['Routing via gateway', 'Streaming tokens live', 'Same credits as the API', 'app gateway models lists routes'],
  },
];

const hostedServices = [
  { label: 'Free app.nz subdomains', detail: 'Claim my-app.app.nz, attach a deploy, get HTTPS.', icon: Globe, href: '/domains' },
  { label: 'Model storage', detail: 'Weights, LoRAs, datasets, and assets next to the GPUs.', icon: HardDrive, href: '/artifacts' },
  { label: 'Hosted model endpoints', detail: 'Serve weights on OpenAI-compatible routes and the same keys.', icon: Server, href: '/cogs' },
  { label: 'One balance, many keys', detail: 'Shared credits, per-project keys, and spend caps.', icon: KeyRound, href: '/api-keys' },
];

const researchModes = [
  { mode: 'Chat', detail: 'Fast chat with uploads, images, and history.', href: '/chat' },
  { mode: 'Web Search', detail: 'Live web results, highlights, and citations.', href: '/search' },
  { mode: 'Deep Research', detail: 'Multi-step search, fetch, and source-backed reports.', href: '/deep-research' },
  { mode: 'Papers', detail: 'Search papers, methods, datasets, and GitHub code free.', href: '/papers' },
];

const gatewayPrices = [
  { label: 'Papers search', price: '$1 / 1,000 API searches · browser free', href: '/papers' },
  { label: 'Exa web search', price: '$0.0077 / request', href: '/search' },
  { label: 'Extra result/page', price: '$0.0011 each', href: '/search' },
  { label: 'GPU compute', price: 'from $0.228/hr', href: '/workers' },
];

// Featured repos link through to the real repo browser, their builds/CI, and the
// live deploy. edukids is the open-source flagship: public source, secure CI
// (secrets are scrubbed from build logs), and a working site at edukids.app.nz.
const repoRows = [
  { repo: 'lee101/edukids', branch: 'main', status: 'live', time: 'open source', href: '/repos/lee101/edukids', ci: 'https://github.com/lee101/edukids/actions', live: 'https://edukids.app.nz' },
  { repo: 'lee101/omniserve', branch: 'master', status: 'live', time: 'open source', href: '/repos/lee101/omniserve', ci: '/ci?repo=lee101/omniserve' },
  { repo: 'leepenkman/cutedsl', branch: 'codex/556-optimize-inference-kernels-for-performance', status: 'review', time: '5w ago', href: '/repos/leepenkman/cutedsl', ci: '/ci?repo=leepenkman/cutedsl' },
  { repo: 'leepenkman/hermes-agent', branch: 'openpaths-provider-integration', status: 'review', time: '4w ago', href: '/repos/leepenkman/hermes-agent', ci: '/ci?repo=leepenkman/hermes-agent' },
  { repo: 'leepenkman/gobed', branch: 'main', status: 'indexed', time: '3mo ago', href: '/repos/leepenkman/gobed', ci: '/ci?repo=leepenkman/gobed' },
];

// Live products built and hosted on app.nz — same repos, agents, gateway, and
// credit balance as everything else on the platform.
const communityApps = [
  { title: 'app.nz/papers', description: 'Free semantic search over 725K ML papers, datasets, and 613K code links.', href: '/papers', icon: BookOpen },
  { title: 'readingtime.app.nz', description: 'Kids picture books generated on demand, with read-along.', href: 'https://readingtime.app.nz', icon: Sparkles },
  { title: 'mojojojo.app.nz', description: 'Python by the millisecond: free in your browser, native-compiled on ours.', href: 'https://mojojojo.app.nz', icon: Zap },
  { title: 'helix.app.nz', description: 'Ask in plain English, get charts and SQL from your data.', href: 'https://helix.app.nz', icon: Activity },
  { title: 'food.app.nz', description: 'Evidence-based ways to optimize everyday foods for nutrition and glucose.', href: 'https://food.app.nz', icon: Zap },
  { title: 'gpubrain.app.nz', description: 'Persistent markdown memory across conversations, over MCP, CLI, and API.', href: 'https://gpubrain.app.nz', icon: Layers },
];

// Sibling products in the app.nz portfolio. Screenshots are real homepage
// captures (visualbench), stored as 480x300 webp in public/assets/portfolio/.
const portfolioSites: Array<{ domain: string; name: string; description: string }> = [
  { domain: 'netwrck.com', name: 'Netwrck', description: 'AI chat, roleplay characters, art creation, and chatrooms.' },
  { domain: 'ebank.nz', name: 'eBank', description: 'AI art generator plus search across generated art.' },
  { domain: 'text-generator.io', name: 'Text Generator', description: 'Text, vision, and speech AI API for developers.' },
  { domain: 'openpaths.io', name: 'OpenPaths', description: 'Open source, OpenAI-compatible AI model router.' },
  { domain: 'codex-infinity.com', name: 'Codex Infinity', description: 'Autonomous AI coding agents on bare-metal workers.' },
  { domain: 'sitesim.net', name: 'SiteSim', description: 'AI website builder: create web pages and games instantly.' },
  { domain: 'webfiddle.net', name: 'WebFiddle', description: 'AI creator for CSS and JavaScript pages you can share.' },
  { domain: 'simplexgen.com', name: 'SimplexGen', description: 'Text to art to 3D model, then sculpt and export in the browser.' },
  { domain: 'dictatorflow.com', name: 'DictatorFlow', description: 'Free local voice dictation for Windows, macOS, and Linux.' },
  { domain: 'ring.nz', name: 'Ring', description: 'AI voice calls: talk to AI characters in real time.' },
  { domain: 'experimentflow.com', name: 'ExperimentFlow', description: 'A/B tests, bandits, and AI personalization for websites.' },
  { domain: 'evangeler.com', name: 'Evangeler', description: 'Affiliate program software and partner marketplace.' },
  { domain: 'hires.nz', name: 'Hires.nz', description: 'NZ marketplace for rentals, services, equipment, and jobs.' },
  { domain: 'how.nz', name: 'How.nz', description: 'AI development blog: kernels, models, and tooling.' },
  { domain: 'bitbank.nz', name: 'BitBank', description: 'AI crypto forecasts, trading bot, and developer API.' },
  { domain: 'v5games.com', name: 'V5 Games', description: 'Text adventure games, AI chatbots, and story games.' },
  { domain: 'addictingwordgames.com', name: 'Addicting Word Games', description: 'Free addicting word games and word puzzles.' },
  { domain: 'wordsmashing.com', name: 'Word Smashing', description: 'Addicting word puzzle game, free to play online.' },
  { domain: 'rewordgame.com', name: 'reWord', description: 'Swap words to make sense of strange sentences.' },
  { domain: 'multiplicationmaster.com', name: 'Multiplication Master', description: 'Multiplication game to train your times tables.' },
  { domain: 'bigmultiplayerchess.com', name: 'Big Multiplayer Chess', description: 'Massive chess board for 6+ players, or play 1v1.' },
];

const faqItems = [
  {
    question: 'What is app.nz?',
    answer:
      'The agent cloud: agents, models, GPUs, and hosting on one balance.',
  },
  {
    question: 'How do cloud coding agents work?',
    answer:
      'Point it at a repo; it branches, writes code, runs checks, opens a PR, and repairs CI or captures VisualBench screenshots.',
  },
  {
    question: 'Can I use my existing OpenAI client?',
    answer:
      'Yes; point it at the app.nz gateway with an API key, then use app/auto or pin a model.',
  },
  {
    question: 'What does pricing look like?',
    answer:
      'Prepaid credits cover agents, deploys, models, search, and GPU time; Pro, Ultra, and Max add monthly credits.',
  },
  {
    question: 'Do I get a public URL for my app?',
    answer:
      'Deploys can use a free app.nz subdomain with HTTPS, preview URLs, and custom domains.',
  },
];

export const PlatformHome: React.FC = () => {
  return (
    <div className="overflow-hidden bg-black text-white">
      <section className="relative min-h-screen overflow-hidden border-b border-white/10 px-4 pb-12 pt-20 sm:px-8 lg:px-12">
        <HeroMeshCanvas />

        <div className="relative mx-auto max-w-[1800px]">
          <div className="hero-content flex flex-col items-center pt-10 text-center lg:pt-14">
            <div className="mb-5 inline-flex flex-wrap items-center justify-center gap-x-1.5 gap-y-1 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/50">
              <Zap size={13} className="text-[#ffc66d]" />
              {heroBadgeLinks.map((link, i) => (
                <React.Fragment key={link.href}>
                  {i > 0 && <span className="text-white/25">·</span>}
                  <a href={link.href} className="transition-colors hover:text-white">
                    {link.label}
                  </a>
                </React.Fragment>
              ))}
            </div>
            <h1 className="max-w-[16ch] font-display text-[2.6rem] font-bold leading-[1] tracking-tight text-white sm:text-6xl sm:leading-[0.94] lg:text-[4.75rem]">
              The agent cloud for building and shipping software.
            </h1>
            <p className="mt-5 max-w-[36rem] text-base leading-7 text-white/70 sm:text-lg sm:leading-8">
              Repos, models, GPUs, hosting, and editors on one account, on one credit balance.
            </p>
            <div className="mt-7 flex flex-col gap-3 sm:flex-row">
              <a
                href="/signup"
                className="inline-flex h-12 items-center justify-center gap-2 rounded-lg bg-sky-400 px-5 text-sm font-bold text-[#1a1408] transition-colors hover:bg-sky-300"
              >
                Start building <ArrowRight size={17} />
              </a>
              <a
                href="#product"
                className="inline-flex h-12 items-center justify-center gap-2 rounded-lg border border-white/12 bg-white/[0.04] px-5 text-sm font-bold text-white transition-colors hover:bg-white hover:text-black"
              >
                Explore platform <ArrowUpRight size={17} />
              </a>
            </div>
            <div className="mt-7 flex flex-wrap items-center justify-center gap-2 text-[11px] font-semibold text-white/64">
              {heroPillars.map(({ icon: Icon, label, href }) => {
                const external = href.startsWith('http');
                return (
                  <a
                    key={label}
                    href={href}
                    {...(external ? { target: '_blank', rel: 'noreferrer' } : {})}
                    className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1.5 transition-colors hover:border-white/25 hover:bg-white/[0.08] hover:text-white"
                  >
                    <Icon size={13} className="text-white/72" />
                    {label}
                  </a>
                );
              })}
            </div>
          </div>

          <div id="product" className="mt-12 grid grid-cols-1 gap-3 text-left sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {productCards.map((card) => (
              <ProductTile key={card.title} card={card} />
            ))}
          </div>
        </div>
      </section>

      <section id="art-playground" className="border-b border-white/10 bg-[#030404] px-4 py-12 sm:px-8 sm:py-16 lg:px-12">
        <div className="mx-auto max-w-[1800px]">
          <div className="mb-8 grid gap-6 lg:grid-cols-[0.82fr_1.18fr] lg:items-end">
            <div>
              <p className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.22em] text-[#ffc66d]">
                <Palette size={15} /> Live Art Playground
              </p>
              <h2 className="mt-3 max-w-4xl font-display text-3xl font-bold leading-tight tracking-tight sm:text-5xl">
                Image, video, and 3D models on one account.
              </h2>
            </div>
            <div>
              <p className="max-w-3xl text-base leading-8 text-white/70">
                Generate, then ship on the same credits.
              </p>
              <div className="mt-5 flex flex-col gap-3 sm:flex-row">
                <a
                  href="/gateway#playground"
                  className="inline-flex h-11 items-center justify-center gap-2 rounded-lg bg-sky-400 px-4 text-sm font-bold text-black transition-colors hover:bg-sky-300"
                >
                  <Play size={16} /> Open playground
                </a>
                <a
                  href="/models?q=art%20generation"
                  className="inline-flex h-11 items-center justify-center gap-2 rounded-lg border border-white/12 bg-white/[0.04] px-4 text-sm font-bold text-white transition-colors hover:bg-white hover:text-black"
                >
                  Browse Art Models <ArrowRight size={16} />
                </a>
                <a
                  href="/art"
                  className="inline-flex h-11 items-center justify-center gap-2 rounded-lg border border-white/12 bg-white/[0.04] px-4 text-sm font-bold text-white/82 transition-colors hover:text-white"
                >
                  Browse Art <ArrowUpRight size={16} />
                </a>
              </div>
            </div>
          </div>

          <div className="grid gap-3 lg:grid-cols-[1.05fr_0.95fr]">
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {mediaCapabilities.map((capability) => (
                <MediaCapabilityTile key={capability.title} capability={capability} />
              ))}
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              {artShowcase.map((item) => (
                <ArtShowcaseCard key={item.title} item={item} />
              ))}
            </div>
          </div>

          <div className="mt-6 border-t border-white/10 pt-6">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/52">app.nz provider coverage</p>
              <a href="/gateway#providers" className="inline-flex items-center gap-1.5 text-sm font-semibold text-white/60 transition-colors hover:text-white">
                Explore providers <ArrowRight size={15} />
              </a>
            </div>
            <div className="flex flex-wrap gap-2">
              {providerBadges.map((provider) => (
                <a
                  key={provider.provider}
                  href={`/models?provider=${encodeURIComponent(provider.provider)}`}
                  className="rounded-full border border-white/10 bg-white/[0.035] px-3 py-1.5 text-xs font-semibold text-white/64 transition-colors hover:border-white/25 hover:bg-white/[0.07] hover:text-white"
                >
                  {provider.label}
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section id="install" className="border-b border-white/10 px-4 py-12 sm:px-8 sm:py-16 lg:px-12">
        <div className="mx-auto max-w-[1800px]">
          <div className="mb-8 text-center">
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#8ebdd4]">Desktop and CLI</p>
            <h2 className="mt-3 font-display text-2xl font-bold tracking-tight sm:text-4xl">One install. Run the agent.</h2>
            <p className="mx-auto mt-4 max-w-xl text-base leading-7 text-white/55">
              The <code className="rounded bg-white/[0.06] px-1.5 py-0.5 font-mono text-sm text-white">app</code> binary is CLI, desktop, and deploys.
            </p>
            <div className="mt-7 flex justify-center">
              <InstallCommand />
            </div>
            <p className="mt-3 text-xs text-white/50">macOS, Windows, and Linux.</p>
          </div>

          <div className="mx-auto mb-8 max-w-4xl">
            <TerminalCast src="/casts/cli-deploy-game.cast" title="app: build and ship a game" rows={19} />
            <p className="mt-2.5 text-center text-xs text-white/48">
              Recorded session, sped up: a cloud agent writes a game; <code className="font-mono text-white/65">app apps deploy</code> ships it to{' '}
              <a href="https://neon-snake.app.nz/" target="_blank" rel="noreferrer" className="text-cyan-300/80 underline decoration-cyan-300/30 hover:text-cyan-200">neon-snake.app.nz</a>.
              {' '}<a href="/cli" className="text-white/70 underline decoration-white/30 hover:text-white">Full command reference →</a>
            </p>
          </div>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {terminalCards.map((card) => (
              <TerminalCard key={card.title} {...card} />
            ))}
          </div>
        </div>
      </section>

      <section id="agents" className="border-b border-white/10 px-4 py-12 sm:px-8 sm:py-16 lg:px-12">
        <div className="mx-auto grid max-w-[1800px] gap-8 lg:grid-cols-[0.9fr_1.1fr]">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#83a66a]">Cloud coding agents</p>
            <h2 className="mt-3 font-display text-2xl font-bold tracking-tight sm:text-4xl">
              Agents that code and ship.
            </h2>
            <p className="mt-5 text-base leading-8 text-white/70">
              Hand off a task; it branches, tests, opens a PR, and deploys.
            </p>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <AgentPanel href="/agent" icon={Bot} title="Plan and implement" body="Branch, sandbox, and iterate until the change is ready." />
            <AgentPanel href="/security" icon={ShieldCheck} title="Prove it before merge" body="Tests, lint, and review before a PR lands." />
            <AgentPanel href="/visualbench" icon={MonitorCog} title="VisualBench proof" body="Screenshot the UI, compare, and fix until it matches." />
            <AgentPanel href="/ci" icon={Activity} title="Repair CI automatically" body="Read logs, patch the regression, and rerun." />
          </div>
        </div>
      </section>

      <section id="research" className="border-b border-white/10 px-4 py-12 sm:px-8 sm:py-16 lg:px-12">
        <div className="mx-auto grid max-w-[1800px] gap-8 lg:grid-cols-[0.86fr_1.14fr]">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#a88bbb]">Research for builders</p>
            <h2 className="mt-3 font-display text-2xl font-bold tracking-tight sm:text-4xl">
              Chat, live web search, papers, and deep research.
            </h2>
            <p className="mt-5 text-base leading-8 text-white/70">
              Ask, search, or read papers, then hand the answer to the agent.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a href="/papers" className="inline-flex h-11 items-center justify-center gap-2 rounded-lg bg-sky-400 px-4 text-sm font-bold text-black transition-colors hover:bg-sky-300">
                <BookOpen size={16} /> Search papers free
              </a>
              <a href="/deep-research" className="inline-flex h-11 items-center justify-center gap-2 rounded-lg border border-white/12 bg-white/[0.04] px-4 text-sm font-bold text-white transition-colors hover:bg-white hover:text-black">
                Open deep research <ArrowRight size={16} />
              </a>
            </div>
          </div>

          <div className="rounded-2xl border border-white/10 bg-[#060606] p-4 shadow-2xl shadow-black/60">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3 border-b border-white/10 pb-4">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-white/10 bg-white/[0.04]">
                  <MessageSquare size={19} className="text-white/70" />
                </div>
                <div>
                  <div className="font-display text-lg font-bold">Research Chat</div>
                  <div className="text-xs text-white/55">web + papers + files + model routing</div>
                </div>
              </div>
              <div className="rounded-full border border-white/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.16em] text-white/46">
                one workspace
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              {researchModes.map((item) => (
                <a
                  key={item.mode}
                  href={item.href}
                  className={`group rounded-xl border p-4 transition-colors hover:border-white/25 hover:bg-white/[0.07] ${
                    item.mode === 'Papers' ? 'border-sky-300/20 bg-sky-400/[0.07]' : 'border-white/10 bg-white/[0.04]'
                  }`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <h3 className="font-display text-base font-bold">{item.mode}</h3>
                    <ArrowRight size={15} className="text-white/35 transition-transform group-hover:translate-x-0.5 group-hover:text-white" />
                  </div>
                  <p className="mt-2 text-sm leading-6 text-white/64">{item.detail}</p>
                </a>
              ))}
            </div>

            <div className="mt-3 rounded-xl border border-white/10 bg-black p-4 font-mono text-xs text-white/58">
              <div className="text-white/36">POST /v1/search</div>
              <div className="mt-2 text-white">{'{"provider":"exa","type":"deep","query":"compare hosted model pricing with citations"}'}</div>
              <div className="mt-3 text-white/36">POST /api/research/start</div>
              <div className="mt-2 text-white">{'{"message":"research model hosting options","web_search":true,"papers":true}'}</div>
            </div>
          </div>
        </div>
      </section>

      <section id="models" className="border-b border-white/10 px-4 py-12 sm:px-8 sm:py-16 lg:px-12">
        <div className="mx-auto max-w-[1800px]">
          <div className="mb-8 grid gap-6 lg:grid-cols-[0.82fr_1.18fr] lg:items-end">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#8ebdd4]">Models</p>
              <h2 className="mt-3 font-display text-2xl font-bold tracking-tight sm:text-4xl">
                One gateway. Route providers or host your own.
              </h2>
            </div>
            <p className="text-base leading-8 text-white/70">
              Point any OpenAI-compatible client at app.nz: chat, images, video, music, speech, 3D, or your own weights.
            </p>
          </div>

          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
            {modelLanes.map((lane) => (
              <div key={lane.model} className="group flex flex-col rounded-xl border border-white/10 bg-white/[0.04] p-5 transition-colors hover:border-white/24 hover:bg-white/[0.055]">
                <a href={lane.href} className="mb-5 flex items-center justify-between gap-3">
                  <Code2 size={20} className="text-white/64" />
                  <span className="rounded-full border border-white/10 px-2 py-1 text-[10px] font-bold uppercase tracking-[0.16em] text-white/55">
                    {lane.route}
                  </span>
                </a>
                <a href={lane.href} className="font-mono text-sm font-bold text-white hover:text-sky-300">{lane.model}</a>
                {lane.tools ? (
                  <div className="mt-3 flex min-h-12 flex-wrap content-start gap-1.5">
                    {lane.tools.map((tool) => (
                      <a
                        key={tool.label}
                        href={tool.href}
                        className="rounded-md border border-white/10 bg-black/30 px-2 py-1 text-xs text-white/70 transition-colors hover:border-white/30 hover:text-white"
                      >
                        {tool.label}
                      </a>
                    ))}
                  </div>
                ) : (
                  <p className="mt-3 min-h-12 text-sm leading-6 text-white/64">{lane.use}</p>
                )}
                <div className="mt-4 flex items-center justify-between border-t border-white/10 pt-4 text-xs text-white/55">
                  <span>{lane.price}</span>
                  <a href={lane.href} className="inline-flex items-center gap-1 text-white/55 hover:text-white">
                    Open <ArrowRight size={13} className="transition-transform group-hover:translate-x-0.5" />
                  </a>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {gatewayPrices.map((item) => (
              <a key={item.label} href={item.href} className="group rounded-xl border border-white/10 bg-white/[0.035] p-4 transition-colors hover:border-white/25 hover:bg-white/[0.06]">
                <div className="flex items-center justify-between gap-3 text-xs font-bold uppercase tracking-[0.18em] text-white/55">
                  {item.label}
                  <ArrowRight size={13} className="transition-transform group-hover:translate-x-0.5 group-hover:text-white" />
                </div>
                <div className="mt-2 font-mono text-sm text-white/72">{item.price}</div>
              </a>
            ))}
          </div>
        </div>
      </section>

      <section id="hosting" className="border-b border-white/10 px-4 py-12 sm:px-8 sm:py-16 lg:px-12">
        <div className="mx-auto max-w-[1800px]">
          <div className="mb-8 flex flex-col justify-between gap-5 md:flex-row md:items-end">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#ffc66d]">Hosting</p>
              <h2 className="mt-3 font-display text-2xl font-bold tracking-tight sm:text-4xl">
                Deploy AI apps, APIs, workers, and model endpoints.
              </h2>
              <p className="mt-4 max-w-2xl text-base leading-8 text-white/70">
                Site Builder for static pages, Build Studio for containers, Cog Studio for GPU inference, and deploys for long-running apps, each with logs, status, and a public URL.
              </p>
            </div>
            <a href="/signup" className="inline-flex h-11 items-center justify-center gap-2 rounded-lg bg-sky-400 px-4 text-sm font-bold text-[#1a1408] hover:bg-sky-300">
              Create a project <ArrowRight size={17} />
            </a>
          </div>

          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
            {hostedServices.map((service) => {
              const Icon = service.icon;
              return (
                <a key={service.label} href={service.href} className="group rounded-xl border border-white/10 bg-white/[0.04] p-5 transition-colors hover:border-white/25 hover:bg-white/[0.06]">
                  <div className="flex items-center justify-between gap-3">
                    <Icon size={24} className="text-white/68" />
                    <ArrowRight size={15} className="text-white/35 transition-transform group-hover:translate-x-0.5 group-hover:text-white" />
                  </div>
                  <h3 className="mt-5 font-display text-lg font-bold">{service.label}</h3>
                  <p className="mt-3 text-sm leading-6 text-white/64">{service.detail}</p>
                </a>
              );
            })}
          </div>
        </div>
      </section>

      <section id="repos" className="border-b border-white/10 px-4 py-12 sm:px-8 sm:py-16 lg:px-12">
        <div className="mx-auto grid max-w-[1800px] gap-8 lg:grid-cols-[0.8fr_1.2fr]">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#83a66a]">Repos at the center</p>
            <h2 className="mt-3 font-display text-2xl font-bold tracking-tight sm:text-4xl">Your code, agents, and deploys share one repo.</h2>
            <p className="mt-5 text-base leading-8 text-white/70">
              Push once. Agents branch, open PRs, and ship previews.
            </p>
          </div>
          <div className="rounded-xl border border-white/10 bg-white/[0.04] p-4">
            {repoRows.map((row) => (
              <div key={row.repo} className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 border-b border-white/10 py-4 last:border-b-0">
                <div className="min-w-0">
                  <a href={row.href} className="group flex items-center gap-2 truncate font-mono text-sm text-white transition-colors hover:text-sky-300">
                    <span className="truncate">{row.repo}</span>
                    <ArrowUpRight size={13} className="shrink-0 opacity-0 transition-opacity group-hover:opacity-100" />
                  </a>
                  <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-white/55">
                    <span className="inline-flex items-center gap-1.5"><GitBranch size={13} />{row.branch}</span>
                    <a href={row.ci} {...(row.ci.startsWith('http') ? { target: '_blank', rel: 'noreferrer' } : {})} className="inline-flex items-center gap-1 text-white/55 transition-colors hover:text-sky-300"><Hammer size={12} /> CI</a>
                    {row.live && <a href={row.live} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-white/55 transition-colors hover:text-sky-300"><Globe size={12} /> Live</a>}
                  </div>
                </div>
                <div className="text-right">
                  <div className="rounded-full border border-white/10 px-2 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-white/58">{row.status}</div>
                  <div className="mt-1 text-xs text-white/55">{row.time}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="community" className="border-b border-white/10 px-4 py-12 sm:px-8 sm:py-16 lg:px-12">
        <div className="mx-auto max-w-[1800px]">
          <div className="mb-8 max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#a88bbb]">Community</p>
            <h2 className="mt-3 font-display text-2xl font-bold tracking-tight sm:text-4xl">Real products running on app.nz.</h2>
            <p className="mt-4 text-base leading-8 text-white/70">
              Live sites on the same agents, models, and credits.
            </p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
            {communityApps.map((app) => {
              const Icon = app.icon;
              const external = app.href.startsWith('http');
              return (
                <a
                  key={app.title}
                  href={app.href}
                  target={external ? '_blank' : undefined}
                  rel={external ? 'noreferrer' : undefined}
                  className="group flex flex-col rounded-xl border border-white/10 bg-white/[0.04] p-5 transition-colors hover:border-white/24 hover:bg-white/[0.055]"
                >
                  <div className="flex items-center justify-between">
                    <Icon size={24} className="text-white/68" />
                    <ArrowUpRight size={15} className="text-white/40 transition-colors group-hover:text-white" />
                  </div>
                  <h3 className="mt-5 font-mono text-sm font-bold text-white">{app.title}</h3>
                  <p className="mt-3 text-sm leading-6 text-white/64">{app.description}</p>
                </a>
              );
            })}
          </div>
        </div>
      </section>

      <section id="portfolio" aria-labelledby="portfolio-heading" className="border-b border-white/10 px-4 py-12 sm:px-8 sm:py-16 lg:px-12">
        <div className="mx-auto max-w-[1800px]">
          <div className="mb-8 max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-white/70">Portfolio</p>
            <h2 id="portfolio-heading" className="mt-3 font-display text-2xl font-bold tracking-tight sm:text-4xl">Products from the app.nz team.</h2>
            <p className="mt-4 text-base leading-8 text-white/72">
              AI apps, developer tools, and games we build and run. Try any of them free.
            </p>
          </div>
          <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {portfolioSites.map((site) => (
              <li key={site.domain}>
                <a
                  href={`https://${site.domain}`}
                  target="_blank"
                  rel="noopener"
                  className="group block overflow-hidden rounded-xl border border-white/10 bg-white/[0.04] transition-colors hover:border-white/30"
                >
                  <img
                    src={`/assets/portfolio/${site.domain}.webp?v=2`}
                    alt={`${site.name} homepage screenshot`}
                    width={480}
                    height={300}
                    loading="lazy"
                    decoding="async"
                    className="aspect-[8/5] w-full border-b border-white/10 bg-white/5 object-cover object-top"
                  />
                  <div className="p-4">
                    <div className="flex items-center justify-between gap-2">
                      <h3 className="font-display text-base font-bold text-white">{site.name}</h3>
                      <ArrowUpRight size={15} aria-hidden="true" className="text-white/60 transition-colors group-hover:text-white" />
                    </div>
                    <p className="mt-1 font-mono text-xs text-white/65">{site.domain}</p>
                    <p className="mt-2 text-sm leading-6 text-white/72">{site.description}</p>
                  </div>
                </a>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section id="faq" className="border-b border-white/10 px-4 py-12 sm:px-8 sm:py-16 lg:px-12">
        <div className="mx-auto max-w-[1800px]">
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#ffc66d]">FAQ</p>
          <h2 id="faq-heading" className="mt-3 font-display text-2xl font-bold tracking-tight sm:text-4xl">
            Common questions
          </h2>
          <div className="mt-8 grid gap-3 lg:grid-cols-2">
            {faqItems.map((item) => (
              <article key={item.question} className="rounded-xl border border-white/10 bg-white/[0.04] p-5">
                <h3 className="font-display text-lg font-bold text-white">{item.question}</h3>
                <p className="mt-3 text-sm leading-7 text-white/64">{item.answer}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden border-b border-white/10 px-4 py-16 text-center sm:px-8 sm:py-28 lg:px-12">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.08),transparent_55%)]" />
        <div className="relative mx-auto max-w-[1800px]">
          <h2 className="mx-auto max-w-3xl font-display text-3xl font-bold tracking-tight sm:text-5xl">
            Ship it on app.nz.
          </h2>
          <p className="mx-auto mt-5 max-w-xl text-base leading-8 text-white/70">
            Images, video, sites. Start free.
          </p>
          <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <a
              href="/signup"
              className="inline-flex h-12 items-center justify-center gap-2 rounded-lg bg-sky-400 px-6 text-sm font-bold text-[#1a1408] transition-colors hover:bg-sky-300"
            >
              Start building <ArrowRight size={17} />
            </a>
            <a
              href="/pricing"
              className="inline-flex h-12 items-center justify-center gap-2 rounded-lg border border-white/12 bg-white/[0.04] px-6 text-sm font-bold text-white transition-colors hover:bg-white hover:text-black"
            >
              See pricing <ArrowUpRight size={17} />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

function HeroMeshCanvas() {
  const canvasRef = React.useRef<HTMLCanvasElement | null>(null);

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    let frame = 0;
    let width = 0;
    let height = 0;
    let dpr = 1;

    const resize = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = canvas.offsetWidth;
      height = canvas.offsetHeight;
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      if (motionQuery.matches) draw(0, false);
    };

    const draw = (time: number, animate = true) => {
      const t = time * 0.00012;
      ctx.clearRect(0, 0, width, height);

      const centerX = width * 0.5;
      const horizonY = height * 0.4;
      const spacing = Math.max(42, Math.min(width, height) / 12);
      const halfColumns = Math.ceil(width / spacing) + 9;
      const rows = 24;

      ctx.save();
      ctx.translate(0, height * 0.02);
      ctx.rotate(-0.03);

      const drawPoint = (x: number, z: number) => {
        const depth = 1 / (1 + z * 0.075);
        const wave = Math.sin(x * 0.015 + z * 0.4 + t * 7) * 15;
        const drift = Math.cos(z * 0.24 + t * 5) * 26;
        return {
          x: centerX + (x + drift) * depth * 1.55,
          y: horizonY + z * spacing * depth * 0.62 + wave * depth,
        };
      };

      const strokeMesh = (path: Array<{ x: number; y: number }>, offset: number) => {
        if (path.length < 2) return;
        const shimmer = (Math.sin(t * 11 + offset) + 1) / 2;
        ctx.beginPath();
        path.forEach((point, index) => {
          if (index === 0) ctx.moveTo(point.x, point.y);
          else ctx.lineTo(point.x, point.y);
        });
        ctx.strokeStyle = `rgba(142, 189, 212,${0.10 + shimmer * 0.18})`;
        ctx.lineWidth = 1;
        ctx.stroke();
      };

      for (let row = 0; row < rows; row++) {
        const z = row + 0.6;
        const path = [];
        for (let col = -halfColumns; col <= halfColumns; col++) path.push(drawPoint(col * spacing, z));
        strokeMesh(path, row * 0.41);
      }
      for (let col = -halfColumns; col <= halfColumns; col++) {
        const x = col * spacing;
        const path = [];
        for (let row = 0; row < rows; row++) path.push(drawPoint(x, row + 0.6));
        strokeMesh(path, col * 0.28);
      }

      ctx.restore();
      if (animate && !motionQuery.matches) frame = requestAnimationFrame((nextTime) => draw(nextTime, true));
    };

    const handleMotionChange = () => {
      cancelAnimationFrame(frame);
      frame = 0;
      draw(0, !motionQuery.matches);
    };

    resize();
    window.addEventListener('resize', resize);
    motionQuery.addEventListener('change', handleMotionChange);
    draw(0, !motionQuery.matches);
    return () => {
      window.removeEventListener('resize', resize);
      motionQuery.removeEventListener('change', handleMotionChange);
      cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <div className="pointer-events-none absolute inset-0">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_42%,rgba(255,198,109,0.08),transparent_42%),radial-gradient(circle_at_62%_38%,rgba(142,189,212,0.10),transparent_48%),linear-gradient(180deg,#0b0d10_0%,#0b0d10_82%)]" />
      <canvas
        ref={canvasRef}
        className="absolute inset-x-0 top-0 h-full w-full opacity-95 [mask-image:linear-gradient(to_bottom,black_0%,black_74%,transparent_100%)]"
        aria-hidden="true"
      />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_52%_46%_at_50%_40%,#000_0%,rgba(0,0,0,0.94)_30%,rgba(0,0,0,0.6)_56%,transparent_78%)]" />
      <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-black to-transparent" />
    </div>
  );
}

function ProductTile({ card }: { card: ProductCard }) {
  const Icon = card.icon;
  const isExternal = card.href.startsWith('http');
  return (
    <a
      href={card.href}
      target={isExternal ? '_blank' : undefined}
      rel={isExternal ? 'noreferrer' : undefined}
      className="group grid min-h-[126px] grid-cols-[44px_minmax(0,1fr)_18px] gap-4 rounded-xl border border-[#d6d0c8]/12 bg-black/40 p-4 backdrop-blur-xl transition-colors hover:border-[#ffc66d]/35 hover:bg-black/55"
    >
      <div className="flex h-11 w-11 items-center justify-center rounded-lg border border-white/10 bg-black">
        <Icon size={22} className="text-[#8ebdd4]" />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/55">{card.eyebrow}</div>
        <h2 className="mt-1 font-display text-lg font-bold text-white">{card.title}</h2>
        <p className="mt-2 line-clamp-2 text-sm leading-5 text-white/64">{card.description}</p>
      </div>
      <ArrowRight size={17} className="mt-1 text-white/55 transition-transform group-hover:translate-x-0.5 group-hover:text-white" />
    </a>
  );
}

function MediaCapabilityTile({ capability }: { capability: MediaCapability }) {
  const Icon = capability.icon;
  const isExternal = capability.href.startsWith('http');
  return (
    <a
      href={capability.href}
      target={isExternal ? '_blank' : undefined}
      rel={isExternal ? 'noreferrer' : undefined}
      className="group flex min-h-[194px] flex-col rounded-lg border border-white/10 bg-white/[0.035] p-5 transition-colors hover:border-white/24 hover:bg-white/[0.055]"
    >
      <div className="flex items-center justify-between gap-3">
        <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-white/10 bg-black/70">
          <Icon size={20} className="text-white/72" />
        </span>
        <ArrowRight size={16} className="text-white/32 transition-transform group-hover:translate-x-0.5 group-hover:text-white" />
      </div>
      <h3 className="mt-5 font-display text-lg font-bold text-white">{capability.title}</h3>
      <div className="mt-2 text-[11px] font-bold uppercase tracking-[0.16em] text-amber-100/58">{capability.label}</div>
      <p className="mt-3 text-sm leading-6 text-white/62">{capability.body}</p>
    </a>
  );
}

function ArtShowcaseCard({ item }: { item: ArtShowcaseItem }) {
  return (
    <a href={item.href} className="group overflow-hidden rounded-lg border border-white/10 bg-black/60 transition-colors hover:border-white/28">
      <div className="relative aspect-[4/3] overflow-hidden">
        <img
          src={item.imageUrl}
          alt={`${item.title} generated with ${item.model}`}
          loading="lazy"
          decoding="async"
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.035]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/25 to-transparent" />
        <div className="absolute left-3 top-3 rounded-full border border-white/15 bg-black/62 px-2.5 py-1 text-[11px] font-semibold text-white/78 backdrop-blur">
          {item.provider}
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/48">{item.model}</div>
          <h3 className="mt-1 font-display text-lg font-bold">{item.title}</h3>
          <p className="mt-2 line-clamp-2 text-sm leading-5 text-white/64">{item.prompt}</p>
        </div>
      </div>
    </a>
  );
}

function InstallCommand() {
  const [copied, setCopied] = React.useState(false);

  const onCopy = async () => {
    const ok = await copyInstallCommand();
    if (ok) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="flex w-full max-w-2xl flex-col items-stretch gap-3 sm:flex-row sm:items-center">
      <button
        type="button"
        onClick={onCopy}
        aria-label={`$ ${INSTALL_COMMAND} — copy install command`}
        className="group flex h-14 flex-1 items-center gap-4 rounded-xl border border-white/12 bg-[#050505] px-5 font-mono text-base text-white transition-colors hover:border-white/30"
      >
        <span className="text-white/55">$</span>
        <span className="min-w-0 flex-1 truncate text-left">{INSTALL_COMMAND}</span>
        {copied ? (
          <span className="inline-flex shrink-0 items-center gap-1.5 text-sm font-sans font-semibold text-white">
            <CheckCircle2 size={17} /> Copied
          </span>
        ) : (
          <Copy size={18} className="shrink-0 text-white/55 transition-colors group-hover:text-white" />
        )}
      </button>
      <a
        href="/repos/leepenkman/app-cli"
        className="inline-flex h-14 shrink-0 items-center justify-center gap-2 rounded-xl border border-white/12 bg-white/[0.04] px-5 text-sm font-semibold text-white transition-colors hover:bg-white hover:text-black"
      >
        View source <Terminal size={16} />
      </a>
    </div>
  );
}

function TerminalCard({ title, command, lines }: { title: string; command: string; lines: string[] }) {
  return (
    <div className="min-h-[174px] rounded-xl border border-white/10 bg-[#050505] p-4 font-mono text-xs">
      <div className="mb-3 flex items-center justify-between gap-3">
        <span className="text-white/48">{title}</span>
        <Copy size={14} className="text-white/55" />
      </div>
      <div className="mb-3 truncate text-white">
        <span className="text-white/55">$</span> {command.replace('$ ', '')}
      </div>
      <div className="space-y-1.5 text-white/52">
        {lines.map((line) => (
          <div key={line} className="flex gap-2">
            <CheckCircle2 size={13} className="mt-0.5 shrink-0 text-[#83a66a]" />
            <span className="truncate">{line}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function AgentPanel({ icon: Icon, title, body, href }: { icon: IconType; title: string; body: string; href: string }) {
  return (
    <a href={href} className="group rounded-xl border border-white/10 bg-white/[0.04] p-5 transition-colors hover:border-white/25 hover:bg-white/[0.06]">
      <div className="flex items-center justify-between gap-3">
        <Icon size={24} className="text-white/68" />
        <ArrowRight size={15} className="text-white/35 transition-transform group-hover:translate-x-0.5 group-hover:text-white" />
      </div>
      <h3 className="mt-5 font-display text-lg font-bold">{title}</h3>
      <p className="mt-3 text-sm leading-6 text-white/64">{body}</p>
    </a>
  );
}
